import { useState } from 'react';
import { useLanguage } from '../useLanguage';

const ModernServices = () => {
  const { t } = useLanguage();
  const [expandedService, setExpandedService] = useState<number | null>(null);

  const services = t.services.servicesList.map((service, index) => ({
    icon: ['ARCH', 'AI', 'TEAM', 'PERF', 'SEC', 'START'][index],
    title: service.title,
    summary: service.summary,
    features: service.features,
    color: ['from-blue-500 to-cyan-500', 'from-purple-500 to-pink-500', 'from-green-500 to-emerald-500', 'from-orange-500 to-red-500', 'from-red-500 to-pink-500', 'from-indigo-500 to-purple-500'][index],
    pricing: service.pricing,
  }));

  return (
    <section id="services" className="section-padding bg-gray-50 dark:bg-dark-800">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {t.services.title}
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            {t.services.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="card p-6 hover:scale-105 hover:shadow-2xl transition-all duration-300 cursor-pointer animate-fade-in"
              style={{animationDelay: `${index * 0.1}s`}}
              onClick={() => setExpandedService(expandedService === index ? null : index)}
            >
              {/* Header */}
              <div className="text-center mb-6">
                <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r ${service.color} flex items-center justify-center text-sm font-bold text-white shadow-lg`}>
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  {service.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-3">
                  {service.summary}
                </p>
                <div className="text-sm font-semibold text-primary-600 dark:text-primary-400">
                  {service.pricing}
                </div>
              </div>

              {/* Expand Button */}
              <div className="text-center mb-4">
                <button className="text-primary-600 dark:text-primary-400 hover:underline font-medium">
                  {expandedService === index ? t.services.showLess : t.services.learnMore}
                </button>
              </div>

              {/* Expanded Content */}
              {expandedService === index && (
                <div className="space-y-3 animate-fade-in">
                  <h4 className="font-semibold text-gray-900 dark:text-white">{t.services.keyFeatures}</h4>
                  <ul className="space-y-2">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-start space-x-2">
                        <span className="text-green-500 mt-1">✓</span>
                        <span className="text-gray-700 dark:text-gray-300 text-sm">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Process Section */}
        <div className="mt-16 mb-16">
          <h3 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-8">{t.services.howItWorks}</h3>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center animate-slide-up" style={{animationDelay: '0.1s'}}>
              <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-4 hover:scale-110 transition-transform">
                <span className="text-primary-600 dark:text-primary-400 font-bold">1</span>
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{t.services.discoveryCall}</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t.services.discoveryDesc}</p>
            </div>
            <div className="text-center animate-slide-up" style={{animationDelay: '0.2s'}}>
              <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-4 hover:scale-110 transition-transform">
                <span className="text-primary-600 dark:text-primary-400 font-bold">2</span>
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{t.services.assessment}</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t.services.assessmentDesc}</p>
            </div>
            <div className="text-center animate-slide-up" style={{animationDelay: '0.3s'}}>
              <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-4 hover:scale-110 transition-transform">
                <span className="text-primary-600 dark:text-primary-400 font-bold">3</span>
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{t.services.implementation}</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t.services.implementationDesc}</p>
            </div>
            <div className="text-center animate-slide-up" style={{animationDelay: '0.4s'}}>
              <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-4 hover:scale-110 transition-transform">
                <span className="text-primary-600 dark:text-primary-400 font-bold">4</span>
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{t.services.ongoingSupport}</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t.services.ongoingSupportDesc}</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white p-8 rounded-2xl shadow-xl">
            <h3 className="text-2xl font-bold mb-4">{t.services.readyToAccelerate}</h3>
            <p className="text-lg mb-6 opacity-90 max-w-2xl mx-auto">
              {t.services.transformCapabilities}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://calendly.com/benjamin-juarez/60min"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-primary-600 hover:bg-gray-100 font-bold py-3 px-8 rounded-lg transition-all duration-200 hover:scale-105 inline-flex items-center justify-center space-x-2"
              >
                <span>{t.services.scheduleFree}</span>
              </a>
              {/* <a
                href="https://ko-fi.com/C0C2QPCEG"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary-600 font-bold py-3 px-8 rounded-lg transition-all duration-200 hover:scale-105 inline-flex items-center justify-center space-x-2"
              >
                <span>Support My Work</span>
              </a> */}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ModernServices;